import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { inrCompact } from '../../lib/format';
import { BarChart3, TrendingUp, Target, Award } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';

export default function MISReports() {
  const [funnelData, setFunnelData] = useState(null);
  const [winLossData, setWinLossData] = useState(null);
  const [forecastData, setForecastData] = useState(null);

  useEffect(() => {
    const loadReports = async () => {
      try {
        const [funnel, winLoss, forecast] = await Promise.all([
          apiGet('/crm/reports/conversion-funnel'),
          apiGet('/crm/reports/win-loss-analysis'),
          apiGet('/crm/reports/sales-forecast')
        ]);
        setFunnelData(funnel);
        setWinLossData(winLoss);
        setForecastData(forecast);
      } catch (err) {
        console.error('Failed to load reports:', err);
      }
    };
    loadReports();
  }, []);

  if (!funnelData || !winLossData || !forecastData) {
    return <div className="p-8 text-sm text-slate-500">Loading MIS reports…</div>;
  }

  const funnelChartData = [
    { stage: 'Leads', count: funnelData.total_leads },
    { stage: 'Opportunities', count: funnelData.qualified_opportunities },
    { stage: 'Proposals', count: funnelData.proposals_submitted },
    { stage: 'Won', count: funnelData.won_projects }
  ];

  const winLossPieData = [
    { name: 'Won', value: winLossData.won_count },
    { name: 'Lost', value: winLossData.lost_count }
  ];

  const COLORS = ['#10B981', '#EF4444'];

  const stageChartData = Object.entries(forecastData.by_stage || {}).map(([name, data]) => ({
    name,
    count: data.count,
    value: data.total_value,
  }));

  return (
    <div>
      <div className="mb-7">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Business Development</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">MIS Reports</h1>
        <p className="text-sm text-slate-600 mt-1">Management Information System reports for sales performance</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.15em] text-slate-500">Lead Conv. Rate</div>
            <Target size={15} className="text-blue-600" />
          </div>
          <div className="font-display text-2xl font-bold text-[#0F172A]">{funnelData.conversion_rates.lead_to_opportunity}%</div>
          <div className="mt-2 text-xs text-slate-600">Leads → Opportunities</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.15em] text-slate-500">Win Rate</div>
            <Award size={15} className="text-emerald-600" />
          </div>
          <div className="font-display text-2xl font-bold text-[#0F172A]">{winLossData.win_rate}%</div>
          <div className="mt-2 text-xs text-slate-600">{winLossData.won_count} won / {winLossData.lost_count} lost</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.15em] text-slate-500">Avg Deal Size</div>
            <TrendingUp size={15} className="text-amber-600" />
          </div>
          <div className="font-display text-2xl font-bold text-[#0F172A]">{inrCompact(winLossData.average_deal_size)}</div>
          <div className="mt-2 text-xs text-slate-600">Per won project</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.15em] text-slate-500">Pipeline Value</div>
            <BarChart3 size={15} className="text-indigo-600" />
          </div>
          <div className="font-display text-2xl font-bold text-[#0F172A]">{inrCompact(forecastData.total_pipeline_value)}</div>
          <div className="mt-2 text-xs text-slate-600">Across all open stages</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Conversion Funnel */}
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-4">Conversion Funnel</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnelChartData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="stage" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} />
              <Bar dataKey="count" fill="#3b4872" radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-slate-600">Lead → Opportunity:</span>
              <span className="font-display font-bold ml-2">{funnelData.conversion_rates.lead_to_opportunity}%</span>
            </div>
            <div>
              <span className="text-slate-600">Opportunity → Won:</span>
              <span className="font-display font-bold ml-2">{funnelData.conversion_rates.opportunity_to_won}%</span>
            </div>
          </div>
        </div>

        {/* Win/Loss Analysis */}
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="font-display font-bold text-lg mb-4">Win/Loss Analysis</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={winLossPieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} strokeWidth={0}>
                {winLossPieData.map((entry, index) => <Cell key={index} fill={COLORS[index]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 bg-emerald-500" />
                <span>Won</span>
              </span>
              <span className="font-display font-bold">{inrCompact(winLossData.total_won_value)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500" />
                <span>Lost</span>
              </span>
              <span className="font-display font-bold">{inrCompact(winLossData.total_lost_value)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Forecast by Stage */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <h3 className="font-display font-bold text-lg mb-4">Sales Forecast by Stage</h3>
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Count</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {stageChartData.map((stage, i) => (
                <tr key={i}>
                  <td className="font-display font-semibold">{stage.name}</td>
                  <td className="text-xs">{stage.count}</td>
                  <td className="font-display font-bold text-xs">{inrCompact(stage.value)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-bold">
                <td className="font-display">Total</td>
                <td></td>
                <td className="font-display text-xs">{inrCompact(forecastData.total_pipeline_value)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
